<template>
  <b-modal
    id="GenerateClinicCode"
    ref="GenerateClinicCode"
    size="lg"
    hide-footer
    no-close-on-backdrop
    centered
    @hide="close"
    @hidden="resetModal"
  >
    <!-- Header Section -->
    <template #modal-title>
      <h5 class="ml-4 mt-2">รหัสเชื่อมคลินิก</h5>
    </template>

    <div class="modal-content-wrapper">
      <div class="clinic-code-container">
        <!-- Warning Alert Section -->
        <div class="warning-alert-section">
          <b-alert
            show
            variant="danger"
            class="mb-4 font-weight-bold text-center"
            style="font-size: 16px"
          >
            <i
              class="fas fa-exclamation-triangle mr-2 animated flash infinite"
            ></i>
            กรุณาเช็คเบอร์โทรศัพท์ของผู้ป่วยให้ถูกต้องก่อนใช้รหัสเชื่อมคลินิก!
          </b-alert>
        </div>

        <!-- Patient Information Section -->
        <div class="verification-section mb-4">
          <div class="section-title">
            <i class="fas fa-user-check mr-2"></i> ข้อมูลสำหรับการยืนยันตัวตน
          </div>
          <div class="phone-number-container">
            <div class="phone-label">
              <i class="fas fa-phone-alt mr-2"></i> เบอร์โทรศัพท์ผู้ป่วย:
            </div>
            <div class="phone-value">
              <span v-if="hasPatientPhone">{{ formattedPhoneNumber }}</span>
              <span v-else class="text-muted">ไม่พบข้อมูลเบอร์โทรศัพท์</span>
            </div>
            <div class="edit-link mt-2">
              <small>
                <i class="fas fa-info-circle mr-1"></i>
                หากเบอร์โทรศัพท์ไม่ถูกต้อง กรุณา
                <a
                  href="#"
                  @click.prevent="navigateToPatientProfile"
                  class="edit-profile-link"
                >
                  <i class="fas fa-edit mr-1"></i>แก้ไขในประวัติของผู้ป่วย
                </a>
              </small>
            </div>
          </div>
        </div>

        <!-- Confirmation Section -->
        <div class="confirmation-section mb-4">
          <b-form-checkbox
            id="checkbox-confirm"
            v-model="isConfirmed"
            name="checkbox-confirm"
            class="custom-checkbox"
            required
            @change="handleConfirmationChange"
          >
            <span class="confirmation-text">
              ข้าพเจ้าได้ตรวจสอบเบอร์โทรศัพท์ของผู้ป่วยแล้วว่าถูกต้อง
            </span>
          </b-form-checkbox>
        </div>

        <!-- Clinic Code Display Section -->
        <div class="clinic-code-section">
          <div class="section-title">
            <i class="fas fa-key mr-2"></i> รหัสเชื่อมคลินิก
          </div>
          <transition name="fade" mode="out-in">
            <div v-if="isConfirmed" class="clinic-code-box" key="unlocked">
              <div class="clinic-code">
                {{ clinicCodeValue }}
              </div>
            </div>
            <div v-else class="clinic-code-box locked" key="locked">
              <div class="clinic-code-locked">
                <i class="fas fa-lock"></i> กรุณายืนยันการตรวจสอบก่อนเข้าถึงรหัส
              </div>
            </div>
          </transition>
          <div class="expiry-notice">
            รหัสนี้จะหมดอายุเวลา {{ formattedExpiryTime }} <br />
            หรือมีการ "สร้างรหัสเชื่อมคลินิก" ใหม่
          </div>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <Loading v-if="isLoading" />
  </b-modal>
</template>

<script>
import Loading from "@/components/Loading";
import moment from "moment";
import { mapActions } from "vuex";
import "@/theme/modal/CheckInList.scss";
import { formatPhoneForDisplay } from "@/utils/phoneUtils";

export default {
  name: "GenerateClinicCode",

  components: {
    Loading,
  },

  props: {
    clinicCode: {
      type: Object,
      required: true,
      default: () => ({
        clinic_code: "",
        expiry_date: null,
      }),
    },
    patientId: {
      type: Number,
      default: null,
    },
    patientData: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      isLoading: false,
      isConfirmed: false,
      isConnected: false,
    };
  },

  computed: {
    /**
     * Check if patient has phone number
     * @returns {Boolean}
     */
    hasPatientPhone() {
      return Boolean(this.patientData?.mobile);
    },

    /**
     * Format patient phone number
     * @returns {String}
     */
    formattedPhoneNumber() {
      return this.formatPhoneNumber(this.patientData?.mobile || "");
    },

    /**
     * Get clinic code value
     * @returns {String}
     */
    clinicCodeValue() {
      return this.clinicCode?.clinic_code || "";
    },

    /**
     * Format expiry time
     * @returns {String}
     */
    formattedExpiryTime() {
      return this.formatTime(this.clinicCode?.expiry_date);
    },
  },

  methods: {
    ...mapActions({
      createClinicCode: "moduleTreatment/createClinicCode",
      fetchPatientConnectClinicStatus:
        "moduleTreatment/fetchPatientConnectClinicStatus",
    }),

    /**
     * Format time to HH:mm
     * @param {String|Date} date - Date to format
     * @returns {String} - Formatted time
     */
    formatTime(date) {
      if (!date) return "";
      return moment(date).format("HH:mm");
    },

    /**
     * Format phone number to Thai format (0xx-xxx-xxxx)
     * @param {String} phone - Phone number to format
     * @returns {String} - Formatted phone number
     */
    formatPhoneNumber(phone) {
      if (!phone) return "";
      return formatPhoneForDisplay(phone);
    },

    /**
     * Handle confirmation checkbox change
     */
    handleConfirmationChange() {
      if (this.isConfirmed && !this.hasPatientPhone) {
        this.$bvToast.toast(
          "ไม่พบข้อมูลเบอร์โทรศัพท์ กรุณาตรวจสอบข้อมูลผู้ป่วย",
          {
            title: "ข้อควรระวัง",
            variant: "warning",
            solid: true,
          }
        );
      }
    },

    /**
     * Check patient connect clinic status
     * @param {Number} patientId - Patient ID
     */
    async checkConnectClinic(patientId) {
      if (!patientId) return;

      try {
        this.isLoading = true;
        const res = await this.fetchPatientConnectClinicStatus({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          patientId,
        });
        this.isConnected = res.data.is_connected;
      } catch (error) {
        console.error("Error checking clinic connection:", error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Show modal
     */
    show() {
      this.$bvModal.show("GenerateClinicCode");
    },

    /**
     * Close modal
     */
    close() {
      this.$bvModal.hide("GenerateClinicCode");
    },

    /**
     * Navigate to patient profile for editing using Customer modal
     */
    navigateToPatientProfile() {
      // เรียกใช้ฟังก์ชันสำหรับเปิด customer.vue modal
      this.$emit("openCustomerModal", Object.assign({}, this.patientData));
      this.close();
    },

    /**
     * Reset modal state
     */
    resetModal() {
      this.isLoading = false;
      this.isConfirmed = false;
    },
  },
};
</script>

<style scoped>
/* ===== Layout Styling ===== */
.modal-content-wrapper {
  padding: 1.5rem;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.clinic-code-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 1rem;
}

/* ===== Section Styling ===== */
.warning-alert-section,
.verification-section,
.confirmation-section,
.clinic-code-section {
  width: 100%;
  max-width: 500px;
  margin-bottom: 20px;
  border-radius: 8px;
}

.verification-section {
  background-color: #edf2f7;
  border-left: 4px solid #4299e1;
  padding: 15px;
}

.confirmation-section {
  padding: 15px;
  background-color: #fff9e6;
  border-radius: 8px;
  border-left: 4px solid #f6ad55;
  display: flex;
  justify-content: center;
}

.clinic-code-section {
  background-color: #f7f9fb;
  border-left: 4px solid #38b2ac;
  padding: 15px 15px 20px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

/* ===== Phone Number Styling ===== */
.phone-number-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f1f8ff;
  padding: 12px 20px;
  border-radius: 6px;
  border: 1px solid #d1e4fc;
  width: 100%;
}

.phone-label {
  font-size: 14px;
  color: #4a5568;
  margin-bottom: 4px;
}

.phone-value {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  letter-spacing: 1px;
}

.edit-link {
  font-size: 13px;
  color: #718096;
  text-align: center;
  width: 100%;
}

.edit-profile-link {
  color: #4299e1;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s ease;
}

.edit-profile-link:hover {
  color: #2b6cb0;
  text-decoration: underline;
}

/* ===== Clinic Code Box Styling ===== */
.clinic-code-box {
  background: linear-gradient(145deg, #e6edf4, #d1e1f1);
  padding: 1.5rem 2rem;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 2px dashed #38b2ac;
  transition: all 0.3s ease;
  margin: 10px auto;
  width: 100%;
}

.clinic-code-box:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}

.clinic-code {
  color: #38b2ac;
  font-size: 36px;
  font-weight: 700;
  text-align: center;
  letter-spacing: 6.4px;
  text-shadow: 1px 1px 1px rgba(255, 255, 255, 0.8);
}

.expiry-notice {
  color: #6c757d;
  font-weight: 500;
  text-align: center;
  font-size: 14px;
  margin-top: 12px;
  line-height: 24px;
}

/* ===== Checkbox Styling ===== */
.custom-checkbox {
  user-select: none;
}

.confirmation-text {
  font-weight: 600;
  color: #4a5568;
}

.custom-checkbox .custom-control-label::before {
  border-color: #f6ad55;
}

.custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #f6ad55;
  border-color: #f6ad55;
}

/* ===== Locked State Styling ===== */
.clinic-code-box.locked {
  background: linear-gradient(145deg, #f0f0f0, #e0e0e0);
  border: 2px dashed #cbd5e0;
}

.clinic-code-locked {
  color: #718096;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  padding: 10px;
}

.clinic-code-locked i {
  font-size: 22px;
  margin-right: 10px;
  color: #a0aec0;
}

/* ===== Animations ===== */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* ===== Responsive Styling ===== */
@media (min-width: 1400px) {
  .col-ul-2 {
    flex: 0 0 20%;
    max-width: 20%;
  }
}
</style>